import React, { useState, useMemo } from 'react';
import { DollarSign, Calendar, Percent, TrendingUp } from 'lucide-react';
import CalculatorInput from '../shared/CalculatorInput';
import ResultCard from '../shared/ResultCard';
import Chart from '../shared/Chart';

const InflationCalculator: React.FC = () => {
  const [currentAmount, setCurrentAmount] = useState(100000);
  const [years, setYears] = useState(20);
  const [inflationRate, setInflationRate] = useState(2.5);

  const calculatedData = useMemo(() => {
    const data = [];
    const monthlyRate = inflationRate / 100 / 12;
    const months = years * 12;

    let amount = currentAmount;

    for (let i = 0; i <= months; i++) {
      if (i > 0) {
        amount = amount * (1 + monthlyRate);
      }

      if (i % 12 === 0) {
        data.push({
          year: i / 12,
          amount: Math.round(amount),
          original: currentAmount,
        });
      }
    }

    return data;
  }, [currentAmount, years, inflationRate]);

  const futureAmount = calculatedData[calculatedData.length - 1]?.amount || 0;
  const purchasingPowerLoss = ((currentAmount - futureAmount) / currentAmount) * 100;

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 sm:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Inflation Calculator</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <CalculatorInput
          label="Current Amount"
          value={currentAmount}
          onChange={setCurrentAmount}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Time Period"
          value={years}
          onChange={setYears}
          icon={<Calendar size={20} />}
          suffix="years"
        />
        <CalculatorInput
          label="Inflation Rate"
          value={inflationRate}
          onChange={setInflationRate}
          icon={<TrendingUp size={20} />}
          suffix="%"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <ResultCard
          title="Future Amount Needed"
          value={futureAmount}
          description="To maintain today's purchasing power"
          trend="up"
        />
        <ResultCard
          title="Purchasing Power Loss"
          value={Math.abs(purchasingPowerLoss)}
          suffix="%"
          description="Decrease in buying power"
          trend="down"
        />
        <ResultCard
          title="Original Amount"
          value={currentAmount}
          description="Today's value"
          trend="neutral"
        />
      </div>

      <Chart data={calculatedData} />
    </div>
  );
};

export default InflationCalculator;